import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Media from 'components/common/Media'
import { getOnClickAndOnKeyPressDownEvents } from 'helpers/getOnClickAndOnKeyPressDownEvents'
import { arrayHasValues } from 'helpers/utils'
import { formatPhoneNumber } from 'helpers/phone-number'
import Text from 'components/common/Text/Text'
import Icon from 'components/common/Icon/Icon'
import PhoneLink from 'components/common/PhoneLink'
import * as UI from './Common.styles'
import {
  EMPTY_STORE_MESSAGE,
  DROP_OFF_TIMES,
  AVAILABLE_TIME,
  CALL_STORE_MESSAGE_PREFIX,
  CALL_STORE_MESSAGE_SUFFIX,
  DISABLED_STATE_CALL_STORE_PREFIX,
  DISABLED_STATE_CALL_STORE_SUFFIX,
  TIME_SLOTS_ID,
  SELECTED_DAY_MESSAGE,
  TIME_SLOTS_TABLE_ID
} from './constants'
import Datepicker from '../Datepicker/index'
import ScheduleBlock from '../ScheduleBlock'
import NextDayTimeTable from './NextDayTimeTable'
import { DROP_OFF_TIME_FIELD_NAME } from 'constant'
import { getFormattedDate } from './../../helpers'
import AppointmentDurationDisclosure from './../DateTimeSelector/AppointmentDurationDisclosure'

const CheckoutDateTimeSelector = ({
  handleDateSelect,
  handleDateTimeSelect,
  dates,
  input: {
    value: {
      time = {}
    } = {}
  } = {},
  currentDate,
  timeBlocks = [],
  expandable,
  expanded,
  toggleHandlers: {
    onClick
  },
  hasStore,
  noStoreMessage = EMPTY_STORE_MESSAGE,
  isCheckout = false,
  threeStepper,
  firstDateSelected,
  setFirstDateSelected,
  storePhoneNumber,
  splitTimeBlocks,
  showNextDay,
  isCurrentDateSelected,
  isMobileState,
  allTimeBlocks = [],
  theme = '',
  isQuickAppointmentPage
}) =>
  <Fragment>
    {
      hasStore ?
        <UI.MyColumns adaptive width="480px auto" special={ !arrayHasValues(timeBlocks) }>
          <Fragment>
            <Datepicker
              currentDate={ currentDate }
              dates={ dates }
              firstDateSelected={ firstDateSelected }
              setFirstDateSelected={ setFirstDateSelected }
              theme={ theme }
              onDateSelect={ handleDateSelect } />
          </Fragment>
          {
            arrayHasValues(timeBlocks) ?
              <UI.Schedule oneColumn={ !threeStepper } id={ TIME_SLOTS_ID }>
                {
                  dates &&
                    <Fragment>
                      {
                        arrayHasValues(timeBlocks) && threeStepper &&
                        <Fragment>
                          <StyledText
                            data-at-appointment-dropoff-title
                            follow_link
                            block
                            centered
                            narrow
                            bolder>
                            { AVAILABLE_TIME }
                          </StyledText>

                          {isQuickAppointmentPage && (
                            <Media.Desktop>
                              <AppointmentDurationDisclosure />
                            </Media.Desktop>
                          )}

                          {
                            showNextDay &&
                            <UI.Description block centered margin={ '15px 0 5px' }>
                              { `${SELECTED_DAY_MESSAGE}`}
                              <Text bold>
                                {`${getFormattedDate(currentDate)}`}
                              </Text>
                            </UI.Description>
                          }
                        </Fragment>
                      }
                      <Media.Desktop>
                        <UI.Times oneColumn={ !threeStepper } id={ TIME_SLOTS_TABLE_ID }>
                          {
                            threeStepper ?
                              <UI.CustomColumns width={ '50% 50%' }>
                                {
                                  splitTimeBlocks.map((timeBlocksSplitted, index) =>
                                    <fieldset key={ index }>
                                      <legend data-sr-only>{ AVAILABLE_TIME }</legend>
                                      {
                                        timeBlocksSplitted.map(({ displayName, value }) =>
                                          <ScheduleBlock
                                            key={ value }
                                            name={ DROP_OFF_TIME_FIELD_NAME }
                                            displayName={ displayName }
                                            value={ value }
                                            date={ currentDate }
                                            selected={ isCurrentDateSelected && time && time.value === value }
                                            theme={ theme }
                                            onSelect={ handleDateTimeSelect } />
                                        )
                                      }
                                    </fieldset>
                                  )
                                }
                              </UI.CustomColumns> :
                              <fieldset>
                                <legend data-sr-only>{ DROP_OFF_TIMES }</legend>
                                {
                                  allTimeBlocks.map(({ displayName, value }) =>
                                    <ScheduleBlock
                                      key={ value }
                                      displayName={ displayName }
                                      value={ value }
                                      isCheckout={ isCheckout }
                                      date={ currentDate }
                                      selected={ isCurrentDateSelected && time && time.value === value }
                                      theme={ theme }
                                      onSelect={ handleDateTimeSelect } />
                                  )
                                }
                              </fieldset>
                          }
                        </UI.Times>
                        {
                          threeStepper && expandable &&
                            <UI.Drop type="button" link { ...getOnClickAndOnKeyPressDownEvents(onClick) }>
                              <Icon
                                alt=""
                                data-at-schedule-less-icon
                                asset="arrow-gray-up"
                                alternative="arrow-up-green"
                                condition={ expanded }
                                width="16px"
                                margin="0 14px 0 0" />
                              <Text
                                data-at-schedule-more-less-link
                                follow-link
                                size="18"
                                weight="500">
                                { expanded ? 'Show Less' : 'See More' }
                              </Text>
                              <Icon
                                data-at-schedule-more-icon
                                alt=""
                                asset="arrow-down-green"
                                alternative="arrow-gray-down"
                                condition={ expanded }
                                width="16px"
                                margin="0 0 0 14px" />
                            </UI.Drop>
                        }
                        {
                          threeStepper &&
                          <UI.Description
                            data-at-appoinment-nostore-message
                            block
                            centered>
                            { `${CALL_STORE_MESSAGE_PREFIX}${formatPhoneNumber(storePhoneNumber)}${CALL_STORE_MESSAGE_SUFFIX}` }
                          </UI.Description>
                        }
                        {
                          !threeStepper &&
                            <UI.Description
                              data-at-appoinment-nostore-message
                              block
                              centered
                              margin="25px auto"
                              padding="0"
                              width="300px">
                              {
                                arrayHasValues(timeBlocks) ?
                                  `${CALL_STORE_MESSAGE_PREFIX}${formatPhoneNumber(storePhoneNumber)}${CALL_STORE_MESSAGE_SUFFIX}` :
                                  `${DISABLED_STATE_CALL_STORE_PREFIX}${formatPhoneNumber(storePhoneNumber)}${DISABLED_STATE_CALL_STORE_SUFFIX}`
                              }
                            </UI.Description>
                        }
                      </Media.Desktop>
                      <Media.Mobile>
                        {
                          <Fragment>
                            <UI.Times>
                              <UI.CustomColumns width={ '50% 50%' }>
                                {
                                  splitTimeBlocks.map((timeBlocksSplitted, index) =>
                                    <fieldset key={ index }>
                                      <legend data-sr-only>{ AVAILABLE_TIME }</legend>
                                      {
                                        timeBlocksSplitted.map(({ displayName, value }) =>
                                          <ScheduleBlock
                                            key={ value }
                                            name={ DROP_OFF_TIME_FIELD_NAME }
                                            displayName={ displayName }
                                            value={ value }
                                            date={ currentDate }
                                            theme={ theme }
                                            selected={ isCurrentDateSelected && time && time.value === value }
                                            onSelect={ handleDateTimeSelect } />
                                        )
                                      }
                                    </fieldset>
                                  )
                                }
                              </UI.CustomColumns>
                            </UI.Times>
                            {
                              expandable &&
                                <UI.Drop type="button" link { ...getOnClickAndOnKeyPressDownEvents(onClick) } >
                                  <Icon
                                    alt=""
                                    data-at-schedule-less-icon
                                    asset="arrow-gray-up"
                                    alternative="arrow-red-up"
                                    condition={ expanded }
                                    margin="0 14px 0 0" />
                                  <Text
                                    data-at-schedule-more-less-link
                                    follow_link
                                    size="18"
                                    weight="500"> {expanded ? 'Show Less' : 'See More'}</Text>
                                  <Icon
                                    data-at-schedule-more-icon
                                    alt=""
                                    asset="arrow-red-down"
                                    alternative="arrow-gray-down"
                                    condition={ expanded }
                                    margin="0 0 0 14px" />
                                </UI.Drop>
                            }
                          </Fragment>
                        }
                        <UI.Message data-at-appoinment-nostore-message leftAlign={ !threeStepper }>
                          { CALL_STORE_MESSAGE_PREFIX }
                          <Media.Mobile><PhoneLink number={ storePhoneNumber } /></Media.Mobile>
                          { CALL_STORE_MESSAGE_SUFFIX }
                        </UI.Message>
                      </Media.Mobile>
                      {
                        showNextDay && threeStepper &&
                        <NextDayTimeTable
                          isMobileState={ isMobileState }
                          dates={ dates }
                          isCurrentDateSelected={ isCurrentDateSelected }
                          currentDate={ currentDate }
                          handleDateTimeSelect={ handleDateTimeSelect }
                          time={ time }
                          onNextDaySelect={ handleDateSelect } />
                      }
                    </Fragment>
                }
              </UI.Schedule> :
              <Fragment>
                <UI.Message data-at-appoinment-nostore-message mobileOnly={ !threeStepper } leftAlign={ !threeStepper }>
                  <Media.Desktop>
                    { `${DISABLED_STATE_CALL_STORE_PREFIX}${formatPhoneNumber(storePhoneNumber)}${DISABLED_STATE_CALL_STORE_SUFFIX}` }
                  </Media.Desktop>
                  <Media.Mobile>
                    { DISABLED_STATE_CALL_STORE_PREFIX }
                    <PhoneLink number={ storePhoneNumber } />
                    { DISABLED_STATE_CALL_STORE_SUFFIX }
                  </Media.Mobile>
                </UI.Message>
              </Fragment>
          }
        </UI.MyColumns> :
        <UI.Message data-at-appoinment-nostore-message>{ noStoreMessage }</UI.Message>
    }
  </Fragment>

CheckoutDateTimeSelector.propTypes = {
  allTimeBlocks: PropTypes.arrayOf(PropTypes.shape()),
  currentDate: PropTypes.string,
  dates: PropTypes.shape({}),
  expandable: PropTypes.bool,
  expanded: PropTypes.bool,
  firstDateSelected: PropTypes.bool,
  handleDateSelect: PropTypes.func,
  handleDateTimeSelect: PropTypes.func,
  handleTimeSelect: PropTypes.func,
  hasStore: PropTypes.bool,
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.shape({
        time: PropTypes.shape({
          displayName: PropTypes.string,
          value: PropTypes.string
        })
      }),
      PropTypes.string
    ])
  }),
  isCheckout: PropTypes.bool,
  isCurrentDateSelected: PropTypes.bool,
  isMobileState: PropTypes.bool,
  isQuickAppointmentPage: PropTypes.bool,
  noStoreMessage: PropTypes.string,
  setFirstDateSelected: PropTypes.func,
  showNextDay: PropTypes.bool,
  splitTimeBlocks: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  storePhoneNumber: PropTypes.string,
  theme: PropTypes.string,
  threeStepper: PropTypes.bool,
  timeBlocks: PropTypes.arrayOf(PropTypes.shape()),
  toggleHandlers: PropTypes.shape({
    onClick: PropTypes.func
  })
}

export default CheckoutDateTimeSelector

const StyledText = styled(Text)`
  ${Media.mobile`
    margin-bottom: 20px;
  `}
`
